import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { createAccountApi, getWebInstallationId } from '../../api/client';
import type { Envelope } from '../../api/contract';
import { captureAuthEpoch, subscribeAuthChange } from '../../app/auth-context';
import { safeReturnTo } from './returnTo';
import { assertYandexIntent, clearYandexIntent, saveYandexIntent, type YandexIntent } from './yandexIntent';

export function useYandexSignIn({ returnTo: requestedReturnTo, emailLinkRecovery = false }: { returnTo: string; emailLinkRecovery?: boolean }) {
  const returnTo = safeReturnTo(requestedReturnTo);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const epoch = useSyncExternalStore(subscribeAuthChange, captureAuthEpoch);
  const api = useMemo(() => createAccountApi(epoch), [epoch]);
  const active = useRef(false);
  const busy = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const start = async () => {
    if (busy.current) return;
    busy.current = true;
    setStarting(true);
    setError('');
    let intent: YandexIntent | undefined;
    try {
      api.assertCurrent();
      intent = { owner: crypto.randomUUID(), epoch: api.epoch,
        purpose: emailLinkRecovery ? 'email-link' : 'sign-in', returnTo };
      saveYandexIntent(intent);
      const owned = intent;
      const startApi = createAccountApi(api.epoch, () => assertYandexIntent(owned));
      const installationId = getWebInstallationId();
      const result = await startApi.apiMutation<
        Envelope<{
          challengeId: string;
          authorizationUrl: string;
          expiresAt: string;
          pollAfterMs: number;
        }>
      >('/auth/web/yandex/challenges', {
        scope: 'web-auth:create',
        body: { installationId, appVersion: 'kazna-web/0.1.0' },
      });
      api.assertCurrent();
      assertYandexIntent(intent);
      if (!active.current) { clearYandexIntent(intent); return; }
      intent = { ...intent, challengeId: result.data.challengeId };
      saveYandexIntent(intent);
      api.assertCurrent();
      assertYandexIntent(intent);
      window.location.assign(result.data.authorizationUrl);
    } catch (caught) {
      if (intent) clearYandexIntent(intent);
      if (active.current) setError(caught instanceof Error ? caught.message : 'Не удалось начать вход');
    } finally { busy.current = false; if (active.current) setStarting(false); }
  };

  return { start, starting, error, clearError: () => setError('') };
}
